import { get } from 'lodash';
import Alert from '@lendi-ui/alert';
import { Heading } from '@lendi-ui/typography';

import { ENTITY_TYPE, FIELD_PATHS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { PostProductSelectContract, RelatedParty } from '@funnels/types';
import {
  ActionButtonsWrapper,
  Container,
  ContentWrapper,
  FunnelGrid,
  FunnelGridUnit,
  HeadingWrapper,
  NextArrowAction,
} from '@funnels/ui-components';
import {
  ContactCompanyAddress,
  ContactCompanyName,
  ContactEmail,
  ContactFirstName,
  ContactLastName,
  ContactPhoneNumber,
  ContactTitle,
} from '@funnels/ui-components/PostProductSelect';
import { getPPSRelatedPartyDisplayFields } from '@funnels/utils';

interface ConveyancerLayoutProps {
  isFromSummary?: boolean;
}

export const ConveyancerLayout = (props: ConveyancerLayoutProps) => {
  const [formValues] = useFormValues();
  const { isFromSummary } = props;

  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const ppsContracts: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);
  const conveyancerSelectContract = ppsContracts.find(
    (pps) => get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.SOLICITOR
  );

  const relatedPartyIndex = relatedParties.findIndex(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) === ENTITY_TYPE.SOLICITOR
  );

  const questions = get(
    { postProductSelect: [conveyancerSelectContract] },
    FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(0),
    []
  );

  const { hasTitle, hasFirstName, hasLastName, hasPhone, hasEmail, hasBusinessName, hasBusinessAddress } =
    getPPSRelatedPartyDisplayFields(questions);

  const heading = 'Please give us details about your conveyancer';
  const info =
    'By giving us these details, you are giving permission for us or the proposed lender to contact them on your behalf if we need more information.';

  return (
    <Container>
      <HeadingWrapper>
        <Heading size="md">{heading}</Heading>
      </HeadingWrapper>
      <ContentWrapper>
        <FunnelGridUnit mt="sm">
          <Alert variant="info">{info}</Alert>
        </FunnelGridUnit>
        {(hasTitle || hasFirstName) && (
          <FunnelGridUnit mt="sm">
            <FunnelGrid gutter="xxs">
              <FunnelGridUnit size={hasTitle && hasFirstName ? 0.3 : 1}>
                {hasTitle && <ContactTitle relatedPartyIndex={relatedPartyIndex} isOptional />}
              </FunnelGridUnit>
              <FunnelGridUnit size={hasTitle && hasFirstName ? 0.7 : 1}>
                {hasFirstName && <ContactFirstName relatedPartyIndex={relatedPartyIndex} isOptional />}
              </FunnelGridUnit>
            </FunnelGrid>
          </FunnelGridUnit>
        )}
        {hasLastName && (
          <FunnelGridUnit mt="sm">
            <ContactLastName relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasPhone && (
          <FunnelGridUnit mt="sm">
            <ContactPhoneNumber relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasEmail && (
          <FunnelGridUnit mt="sm">
            <ContactEmail relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasBusinessName && (
          <FunnelGridUnit mt="sm">
            <ContactCompanyName label={'Business name'} relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
        {hasBusinessAddress && (
          <FunnelGridUnit mt="sm">
            <ContactCompanyAddress relatedPartyIndex={relatedPartyIndex} isOptional />
          </FunnelGridUnit>
        )}
      </ContentWrapper>
      <FunnelGridUnit>
        <ActionButtonsWrapper>
          <NextArrowAction variant={'primary'} isFullWidth customText={isFromSummary ? 'Save' : 'Next'} />
        </ActionButtonsWrapper>
      </FunnelGridUnit>
    </Container>
  );
};
